import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ErrorBox from "./ErrorBox";
import { classifyError } from "../../utils/classifyError.js";
import { getGenericErrorCopy } from "../../utils/genericErrorCopy.js";

const ADMIN_EMAIL = "registrace@b7id.cz";

/**
 * Renders spa-level message text, linking the admin email when present.
 * @param {string} message
 * @returns {import("react").ReactNode}
 */
function renderSpaMessage(message) {
  const parts = String(message).split(ADMIN_EMAIL);
  if (parts.length === 1) {
    return message;
  }
  return parts.reduce((nodes, part, index) => {
    if (index === 0) {
      return [part];
    }
    return [
      ...nodes,
      <a key={`mail-${index}`} href={`mailto:${ADMIN_EMAIL}`}>
        {ADMIN_EMAIL}
      </a>,
      part,
    ];
  }, []);
}

/**
 * Full-page style error display with icon, explanation text and error box.
 * Use for app/route-level errors (session failures, ErrorBoundary with spaLevel, etc.).
 * @param {Object} props
 * @param {Error} props.error - Error object to display
 */
function SpaLevelErrorDisplay({ error }) {
  const copy = getGenericErrorCopy(classifyError(error), { surface: "spa" });

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
          px: 2,
          minHeight: 200,
          width: "100%",
          maxWidth: 600,
        }}>
        <ErrorOutlineIcon sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3, textAlign: "center", whiteSpace: "pre-line" }}>
          {renderSpaMessage(copy.message)}
        </Typography>
        <ErrorBox error={error} sx={{ width: "100%" }} />
      </Box>
    </Box>
  );
}

SpaLevelErrorDisplay.propTypes = {
  error: PropTypes.object,
};

export default SpaLevelErrorDisplay;
